import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Download, 
  Smartphone, 
  Monitor, 
  CheckCircle2, 
  Share, 
  PlusSquare,
  Chrome,
  Apple,
  Wifi,
  Bell,
  Zap
} from 'lucide-react';

export default function AppDownload() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Check platform
    const userAgent = navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    setIsAndroid(/android/.test(userAgent));

    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone;
    setIsInstalled(standalone);

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', () => setIsInstalled(true));

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const features = [
    { icon: Zap, text: 'Быстрый доступ с рабочего стола' },
    { icon: Wifi, text: 'Работает без интернета' },
    { icon: Bell, text: 'Push-уведомления о рейсах' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-xl">
          <img src="/icon-96.png" alt="BIGBUS" className="w-12 h-12 rounded-xl" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Приложение BIGBUS</h2>
          <p className="text-gray-500">Установите для быстрого доступа</p>
        </div>
      </div>

      {/* Status */}
      {isInstalled ? (
        <Card className="bg-green-50 border-green-200 p-4">
          <div className="flex items-center gap-3 text-green-700">
            <CheckCircle2 className="w-6 h-6" />
            <div>
              <p className="font-semibold">Приложение установлено!</p>
              <p className="text-sm text-green-600">Вы можете открыть его с рабочего стола</p>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="p-6">
          <div className="space-y-6">
            {/* Features */}
            <div className="grid gap-3">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 text-gray-600">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-blue-500" />
                  </div>
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Install buttons by platform */}
            <div className="border-t pt-6 space-y-4">
              {/* Android / Desktop - direct install */}
              {deferredPrompt && (
                <Button 
                  onClick={handleInstall}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2 h-12"
                >
                  <Download className="w-5 h-5" />
                  Установить сейчас
                </Button>
              )}

              {/* Platform-specific instructions */}
              <div className="space-y-4">
                {/* Android */}
                <div className={`p-4 rounded-xl border ${isAndroid ? 'border-green-200 bg-green-50' : 'border-gray-100'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isAndroid ? 'bg-green-500' : 'bg-gray-200'}`}>
                      <Smartphone className={`w-5 h-5 ${isAndroid ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Android</h3>
                      {isAndroid && <span className="text-xs text-green-600">Ваше устройство</span>}
                    </div>
                  </div>
                  <ol className="text-sm text-gray-600 space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium shrink-0">1</span>
                      <span>Откройте меню браузера (⋮)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium shrink-0">2</span>
                      <span>Нажмите <strong>&quot;Установить приложение&quot;</strong> или <strong>&quot;Добавить на главный экран&quot;</strong></span>
                    </li>
                  </ol>
                </div>

                {/* iOS */}
                <div className={`p-4 rounded-xl border ${isIOS ? 'border-blue-200 bg-blue-50' : 'border-gray-100'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isIOS ? 'bg-blue-500' : 'bg-gray-200'}`}>
                      <Apple className={`w-5 h-5 ${isIOS ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">iPhone / iPad</h3>
                      {isIOS && <span className="text-xs text-blue-600">Ваше устройство</span>}
                    </div>
                  </div>
                  <ol className="text-sm text-gray-600 space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium shrink-0">1</span>
                      <span className="flex items-center gap-1">
                        Нажмите <Share className="w-4 h-4 text-blue-500" /> <strong>Поделиться</strong>
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium shrink-0">2</span>
                      <span className="flex items-center gap-1">
                        Выберите <PlusSquare className="w-4 h-4 text-blue-500" /> <strong>На экран «Домой»</strong>
                      </span>
                    </li>
                  </ol>
                </div>

                {/* Windows/Mac */}
                <div className={`p-4 rounded-xl border ${!isIOS && !isAndroid ? 'border-purple-200 bg-purple-50' : 'border-gray-100'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${!isIOS && !isAndroid ? 'bg-purple-500' : 'bg-gray-200'}`}>
                      <Monitor className={`w-5 h-5 ${!isIOS && !isAndroid ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Windows / Mac</h3>
                      {!isIOS && !isAndroid && <span className="text-xs text-purple-600">Ваше устройство</span>}
                    </div>
                  </div>
                  <ol className="text-sm text-gray-600 space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium shrink-0">1</span>
                      <span className="flex items-center gap-1">
                        В адресной строке нажмите <Chrome className="w-4 h-4" /> иконку установки
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium shrink-0">2</span>
                      <span>Подтвердите <strong>&quot;Установить&quot;</strong></span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Additional info */}
      <div className="text-center text-sm text-gray-400">
        <p>Приложение весит менее 1 МБ и обновляется автоматически</p>
      </div>
    </div>
  );
}
